import config from "../config/environment";

const GO_BACKEND_URL = config.GO_BACKEND_URL;

function isSupported() {
  return (
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
  );
}

async function platformAuthenticatorAvailable() {
  if (!isSupported()) return false;
  return window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
}

function b64uToBuffer(str) {
  if (typeof str !== "string") {
    console.error("[webauthn] b64uToBuffer received non-string:", str, "type:", typeof str);
    throw new Error(`WebAuthn decode error: expected base64url string, got ${typeof str}`);
  }
  const pad = "=".repeat((4 - (str.length % 4)) % 4);
  const b64 = (str + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out.buffer;
}

function bufferToB64u(buffer) {
  if (!buffer || typeof buffer.byteLength !== "number") {
    console.error("[webauthn] bufferToB64u received invalid buffer:", buffer, "type:", typeof buffer);
    return undefined;
  }
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function preformatCredentialOptions(options) {
  const out = { ...options };
  out.challenge = b64uToBuffer(options.challenge);
  if (options.user && options.user.id) {
    out.user = { ...options.user, id: b64uToBuffer(options.user.id) };
  }
  if (options.excludeCredentials) {
    out.excludeCredentials = options.excludeCredentials.map((c) => ({
      ...c,
      id: b64uToBuffer(c.id),
    }));
  }
  if (options.allowCredentials) {
    out.allowCredentials = options.allowCredentials.map((c) => ({
      ...c,
      id: b64uToBuffer(c.id),
    }));
  }
  return out;
}

function preformatCredential(credential) {
  console.log("[webauthn] preformatCredential input:", credential);
  console.log("[webauthn] credential.id:", credential.id, "type:", credential.type, "rawId:", credential.rawId);
  console.log("[webauthn] credential.response:", credential.response);
  if (credential.response) {
    console.log(
      "[webauthn] response fields: clientDataJSON",
      credential.response.clientDataJSON,
      "attestationObject:",
      credential.response.attestationObject,
      "authenticatorData:",
      credential.response.authenticatorData,
      "signature:",
      credential.response.signature,
      "userHandle:",
      credential.response.userHandle
    );
  }
  return {
    id: credential.id,
    rawId: bufferToB64u(credential.rawId),
    response: {
      clientDataJSON: bufferToB64u(credential.response.clientDataJSON),
      attestationObject: credential.response.attestationObject
        ? bufferToB64u(credential.response.attestationObject)
        : undefined,
      authenticatorData: credential.response.authenticatorData
        ? bufferToB64u(credential.response.authenticatorData)
        : undefined,
      signature: credential.response.signature
        ? bufferToB64u(credential.response.signature)
        : undefined,
      userHandle: credential.response.userHandle
        ? bufferToB64u(credential.response.userHandle)
        : undefined,
    },
    type: credential.type,
  };
}

async function fetchJson(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
    delete options.token;
  }
  const resp = await fetch(`${GO_BACKEND_URL}${path}`, {
    ...options,
    headers,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    console.error("[webauthn] fetchJson error response:", resp.status, text);
    const data = text ? JSON.parse(text) : {};
    const err = (data && data.error) || `HTTP ${resp.status}`;
    throw new Error(err);
  }
  return resp.json();
}

async function beginLogin() {
  const resp = await fetchJson("/webauthn/login/begin", {
    method: "POST",
    body: JSON.stringify({}),
  });
  console.log("[webauthn] login/begin response:", resp);
  const { session, options } = resp;
  console.log("[webauthn] login options.challenge:", options?.challenge, "options.allowCredentials:", options?.allowCredentials);
  const publicKey = preformatCredentialOptions(options);
  console.log("[webauthn] login preformatted publicKey:", publicKey);
  return { session, publicKey };
}

async function finishLogin(session, credential) {
  const body = preformatCredential(credential);
  console.log("[webauthn] login credential body to send:", body);

  const data = await fetchJson(`/webauthn/login/finish?session=${encodeURIComponent(session)}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  console.log("[webauthn] login/finish response:", data);

  if (!data.token) {
    throw new Error("Missing token in response");
  }
  return data.token;
}

async function login() {
  const available = await platformAuthenticatorAvailable();
  console.log("[webauthn] login begin, platform available:", available);
  if (!available) {
    throw new Error("Face ID / Touch ID not available on this device.");
  }

  const { session, publicKey } = await beginLogin();

  const credential = await navigator.credentials.get({ publicKey });
  console.log("[webauthn] navigator.credentials.get result:", credential);
  if (!credential) throw new Error("Passkey cancelled");

  return finishLogin(session, credential);
}

async function register(token, name = "") {
  const available = await platformAuthenticatorAvailable();
  if (!available) {
    throw new Error("Face ID / Touch ID not available on this device.");
  }
  console.log("[webauthn] register begin, name:", name);

  const beginResp = await fetchJson("/webauthn/register/begin", {
    method: "POST",
    token,
    body: JSON.stringify({}),
  });
  console.log("[webauthn] register/begin response:", beginResp);

  const { session, options } = beginResp;
  console.log("[webauthn] session:", session);
  console.log("[webauthn] raw options:", options);
  console.log("[webauthn] options.challenge:", options?.challenge, "options.user:", options?.user);

  const publicKey = preformatCredentialOptions(options);
  console.log("[webauthn] preformatted publicKey:", publicKey);

  const credential = await navigator.credentials.create({ publicKey });
  console.log("[webauthn] navigator.credentials.create result:", credential);
  if (!credential) throw new Error("Registration cancelled");

  const body = preformatCredential(credential);
  console.log("[webauthn] credential body to send:", body);

  const params = new URLSearchParams({ session });
  if (name) params.set("name", name);
  await fetchJson(`/webauthn/register/finish?${params.toString()}`, {
    method: "POST",
    token,
    body: JSON.stringify(body),
  });
  console.log("[webauthn] register/finish succeeded");
}

async function listCredentials(token) {
  return fetchJson("/webauthn/credentials", { method: "GET", token });
}

async function deleteCredential(token, id) {
  return fetchJson(`/webauthn/credentials/${id}`, {
    method: "DELETE",
    token,
  });
}

const webauthnService = {
  isSupported,
  platformAuthenticatorAvailable,
  login,
  beginLogin,
  finishLogin,
  register,
  listCredentials,
  deleteCredential,
};

export default webauthnService;
